import React, { useEffect, useRef, useCallback, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { xtractAPI } from 'actions/apiActions'
import { useDispatch } from 'react-redux'
import Waiting from './Waiting.jsx'

const StatusCheck = ({ setStatusCheckComplete, subProvider }) => {
  const dispatch = useDispatch()
  const [awaitingStatusCall, setAwaitingStatusCall] = useState(false)
  const [maintRecheckTime, setMaintRecheckTime] = useState(0)
  const [intervalTimer, setIntervalTimer] = useState(undefined)
  const [maintMode, setMaintMode] = useState(false)
  const [expectation, setExpectation] = useState(null)
  // since maintRecheckTime will be read from a callback we have to pass it in as a ref for it to
  // update properly
  const maintRecheckTimeRef = useRef(0)

  useEffect(() => {
    maintRecheckTimeRef.current = maintRecheckTime
  }, [maintRecheckTime])

  const maintMode200Callback = useCallback(
    result => {
      setAwaitingStatusCall(false)
      if (!result.data.data.system_status.maintenance_mode) {
        // maint over
        clearInterval(intervalTimer)
        setMaintRecheckTime(undefined)
        setMaintMode(false)
        setExpectation(null)
        setStatusCheckComplete(true)
      } else {
        setMaintMode(true)
        setMaintRecheckTime(60)
        if (result?.data?.data?.system_status?.expected_back) {
          setExpectation(result.data.expected_back)
        }
        if (!maintMode) {
          // if we are already in maint mode the interval timer is already running
          setIntervalTimer(setInterval(maintModeCheck, 1000))
        }
      }
    },
    [maintMode]
  )

  const maintMode200CallbackRef = useRef(0)

  useEffect(() => {
    maintMode200CallbackRef.current = maintMode200Callback
  }, [maintMode200Callback])

  const maintModeErrorCallback = useCallback(
    result => {
      if (result?.data?.expected_back) {
        setExpectation(result.data.expected_back)
      }
      setAwaitingStatusCall(false)
      setMaintMode(true)
      setMaintRecheckTime(60)
      if (!maintMode) {
        // if we are already in maint mode the interval timer is already running
        setIntervalTimer(setInterval(maintModeCheck, 1000))
      }
    },
    [maintMode]
  )

  const maintModeErrorCallbackRef = useRef(0)

  useEffect(() => {
    maintModeErrorCallbackRef.current = maintModeErrorCallback
  }, [maintModeErrorCallback])

  const maintModeCheck = useCallback(() => {
    const newRecheckSeconds = maintRecheckTimeRef.current - 1
    if (newRecheckSeconds <= 0 && !awaitingStatusCall) {
      const callbacks = {
        200: result => maintMode200CallbackRef.current(result),
        503: maintModeErrorCallbackRef.current,
        error: maintModeErrorCallbackRef.current
      }
      // start the next interval.. we will clear it when the call returns if we are done.
      setMaintRecheckTime(60)
      dispatch(xtractAPI('system_status', 'read', null, callbacks))
    } else {
      setMaintRecheckTime(newRecheckSeconds)
    }
  }, [awaitingStatusCall, maintRecheckTimeRef])

  useEffect(() => {
    // componentDidMount
    if (subProvider !== 'xtract') {
      maintModeCheck()
    } else {
      setStatusCheckComplete(true)
    }
    return () => {
      // componentWillUnmount
      if (intervalTimer != null) {
        clearInterval(intervalTimer)
      }
      maintMode200CallbackRef.current = () => null
    }
  }, [])

  const recheck = maintRecheckTime ? (
    <div
      style={{
        marginTop: '15px',
        fontSize: '2em',
        display: 'flex',
        justifyContent: 'center'
      }}
    >{`Rechecking in ${maintRecheckTime} seconds...`}</div>
  ) : null

  const expectedBack = expectation ? (
    <div
      style={{
        marginTop: '15px',
        fontSize: '1.5em',
        display: 'flex',
        justifyContent: 'center'
      }}
    >
      {expectation}
    </div>
  ) : null

  if (maintMode) {
    return (
      <div className="login-spinner">
        <div
          style={{ marginBottom: '15px', marginTop: '30px' }}
          className="login-spinner-message"
        >
          {'Summit is currently undergoing maintenance.'}
        </div>
        <FontAwesomeIcon icon={['fas', 'tools']} size="5x" fixedWidth />
        {expectedBack}
        {recheck}
      </div>
    )
  } else {
    return <Waiting message={'Checking system status...'} />
  }
}

export default StatusCheck
